import { useT, type Translate } from "../../../../shared/i18n/i18n";
import type { WorkObjectFieldDefinition } from "../../../../entities/work-object-field/model/use-work-object-fields";
import type { CreateWorkObjectInput, WorkObjectListView } from "../../../../entities/work-object/model/use-work-objects";
import {
  editableValueFieldDefinitions,
  isLegacyDescriptionField,
  isMultiValueField,
  isRelationField,
  type WorkObjectFieldValueDraft,
} from "../../../../entities/work-object/model/work-object-field-drafts";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { EditableText } from "@/shared/ui/editable-text";
import { useCreateWorkObjectForm } from "../model/use-create-work-object-form";
import { WorkObjectFieldValueEditor } from "../../../../entities/work-object/ui/work-object-field-editor";

type CreateWorkObjectFormProps = {
  defaultTypeId?: string | null;
  fieldDefinitions: WorkObjectFieldDefinition[];
  isCreating: boolean;
  listView: WorkObjectListView;
  onCreate: (input: CreateWorkObjectInput) => Promise<void>;
  rememberKey?: string | null;
};

// Типы, которые форма не рисует сама, а отдаёт общему редактору полей.
const SHARED_EDITOR_TYPES = new Set<WorkObjectFieldDefinition["type"]>(["currency", "duration", "number", "percent", "rating"]);

export function CreateWorkObjectForm({ defaultTypeId, fieldDefinitions, isCreating, listView, onCreate, rememberKey }: CreateWorkObjectFormProps) {
  const t = useT();
  const form = useCreateWorkObjectForm({ defaultTypeId, fieldDefinitions, listView, onCreate, rememberKey });
  const selectedType = listView.types.find((type) => type.id === form.typeId);
  // The rich description lives in the item's document (added in the detail
  // dialog); the legacy description field stays out of the create flow.
  const editableFields = editableValueFieldDefinitions(form.fieldDefinitions);
  const customFields = editableFields.filter((field) => !isLegacyDescriptionField(field));
  // Связи отделяются от остальных полей: они ведут себя иначе — их выбирают
  // из списка карточек, а не набирают, — и в форме им место в одной строке.
  const relationFields = customFields.filter((field) => isRelationField(field.type) && !field.type.startsWith("user_"));
  const plainFields = customFields.filter((field) => !relationFields.includes(field));

  return (
    <form className="flex flex-col" onSubmit={form.submit}>
      <section className="flex flex-col gap-4 p-5">
        <div className="flex items-start gap-3">
          <EditableText
            ariaLabel={t("board.workItemTitle")}
            className="min-w-0 flex-1 text-base font-semibold leading-6 md:text-sm"
            onChange={form.setTitle}
            placeholder={t("board.untitledItem")}
            value={form.title}
          />

          <TypeBadgeSelect
            onValueChange={form.setTypeId}
            selectedType={selectedType}
            types={listView.types}
            value={form.typeId}
          />
        </div>

        {/* Связи — одной строкой кнопок, а не столбцом полей во всю ширину.
            Заполняют их редко и по одной, а места они занимали больше, чем
            название карточки: форма превращалась в простыню ещё до того, как
            человек дошёл до сути. */}
        {relationFields.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2 border-y border-border/45 px-3 py-2.5">
            {relationFields.map((field) => (
              <div className="flex min-w-0 items-center gap-1.5" key={field.id}>
                <span className="shrink-0 text-xs text-muted-foreground">{field.name}</span>
                <WorkObjectFieldValueEditor
                  field={field}
                  listView={listView}
                  onCommit={(value) => form.setFieldValueDrafts((current) => ({ ...current, [field.id]: value }))}
                  surface="form"
                  value={form.fieldValueDrafts[field.id] ?? (isMultiValueField(field.type) ? [] : "")}
                />
              </div>
            ))}
          </div>
        ) : null}

        {plainFields.length > 0 ? (
          <div className="divide-y divide-border/45 border-b border-border/45">
            {plainFields.map((field) => (
              <CompactFieldRow
                key={field.id}
                field={field}
                listView={listView}
                onChange={(value) =>
                  form.setFieldValueDrafts((current) => ({
                    ...current,
                    [field.id]: value,
                  }))
                }
                value={form.fieldValueDrafts[field.id] ?? (isMultiValueField(field.type) ? [] : "")}
              />
            ))}
          </div>
        ) : null}

        {form.error ? (
          <Alert variant="destructive">
            <AlertDescription>{form.error}</AlertDescription>
          </Alert>
        ) : null}
      </section>

      <div className="flex items-center justify-end border-t border-border/60 px-5 py-4">
        <Button disabled={!form.canSubmit || isCreating} type="submit">
          {isCreating ? t("common.creating") : t("common.create")}
        </Button>
      </div>
    </form>
  );
}

function TypeBadgeSelect({
  onValueChange,
  selectedType,
  types,
  value,
}: {
  onValueChange: (value: string) => void;
  selectedType?: WorkObjectListView["types"][number];
  types: WorkObjectListView["types"];
  value: string;
}) {
  const t = useT();
  return (
    <Select
      onValueChange={(nextValue) => {
        if (nextValue !== null) {
          onValueChange(nextValue);
        }
      }}
      value={value}
    >
      <SelectTrigger
        aria-label={t("fieldType.type")}
        className={cn(
          "h-6 rounded-full border px-2 py-0 text-[0.625rem] font-medium shadow-none",
          typeBadgeClass(selectedType?.category?.key),
        )}
        size="sm"
      >
        <span className="flex min-w-0 flex-1 items-center truncate text-left" data-slot="select-value">
          {selectedType?.name ?? t("fieldType.type")}
        </span>
      </SelectTrigger>
      <SelectContent align="end" alignItemWithTrigger={false} className="min-w-40">
        <SelectGroup>
          {types.map((type) => (
            <SelectItem key={type.id} value={type.id}>
              <span className={cn("size-2 rounded-full", typeDotClass(type.category?.key))} />
              {type.name}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function CompactFieldRow({
  field,
  listView,
  onChange,
  value,
}: {
  field: WorkObjectFieldDefinition;
  listView: WorkObjectListView;
  onChange: (value: WorkObjectFieldValueDraft) => void;
  value: WorkObjectFieldValueDraft;
}) {
  const t = useT();
  const selectedValues = Array.isArray(value) ? value : value ? [value] : [];

  return (
    <div className="grid min-h-10 grid-cols-[8rem_minmax(0,1fr)] items-start gap-3 px-3 py-2 text-xs">
      <span className="pt-1 text-muted-foreground">{field.name}</span>
      {field.type === "select" ? (
        <FieldOptionSelect
          field={field}
          onChange={onChange}
          value={typeof value === "string" ? value : ""}
        />
      ) : field.type === "multi_select" ? (
        <FieldOptionChecklist
          field={field}
          onChange={onChange}
          selectedValues={selectedValues}
        />
      ) : field.type === "checkbox" ? (
        <Checkbox
          aria-label={field.name}
          checked={value === "true"}
          onCheckedChange={(checked) => onChange(checked ? "true" : "false")}
        />
      ) : field.type === "date_range" ? (
        <div className="grid grid-cols-2 gap-2">
          <Input
            aria-label={t("a11y.rangeStart", { field: field.name })}
            className="h-7 border-transparent bg-transparent px-0 py-0 shadow-none focus-visible:border-transparent focus-visible:ring-0"
            onChange={(event) => onChange([event.target.value, selectedValues[1] ?? ""])}
            type="date"
            value={selectedValues[0] ?? ""}
          />
          <Input
            aria-label={t("a11y.rangeEnd", { field: field.name })}
            className="h-7 border-transparent bg-transparent px-0 py-0 shadow-none focus-visible:border-transparent focus-visible:ring-0"
            onChange={(event) => onChange([selectedValues[0] ?? "", event.target.value])}
            type="date"
            value={selectedValues[1] ?? ""}
          />
        </div>
      ) : field.type === "textarea" ? (
        <Textarea
          aria-label={field.name}
          className="min-h-8 border-transparent bg-transparent px-0 py-0 shadow-none focus-visible:border-transparent focus-visible:ring-0"
          onChange={(event) => onChange(event.target.value)}
          placeholder={t("board.addContext")}
          value={typeof value === "string" ? value : value.join("\n")}
        />
      ) : isRelationField(field.type) || SHARED_EDITOR_TYPES.has(field.type) ? (
        // Тот же редактор, что и в карточке. Для связей — поисковый комбобокс
        // вместо списка чекбоксов по ВСЕМ карточкам области (на живом трекере
        // это сотни строк). Для чисел, рейтинга и длительности — потому что
        // нативный <input type="number"> рисует браузерные стрелки, по которым
        // невозможно попасть, а поле в карточке и в форме обязано вести себя
        // одинаково.
        <WorkObjectFieldValueEditor field={field} listView={listView} onCommit={onChange} surface="form" value={value} />
      ) : (
        <Input
          aria-label={field.name}
          className="h-7 border-transparent bg-transparent px-0 py-0 shadow-none focus-visible:border-transparent focus-visible:ring-0"
          onChange={(event) => onChange(event.target.value)}
          type={inputTypeForField(field.type)}
          value={Array.isArray(value) ? value.join("\n") : value}
        />
      )}
    </div>
  );
}

function FieldOptionSelect({
  field,
  onChange,
  value,
}: {
  field: WorkObjectFieldDefinition;
  onChange: (value: string) => void;
  value: string;
}) {
  const t = useT();
  const selectedOption = field.options.find((option) => option.id === value);

  return (
    <Select
      onValueChange={(nextValue) => {
        if (nextValue !== null) {
          onChange(nextValue);
        }
      }}
      value={value}
    >
      <SelectTrigger aria-label={field.name} className="h-7 w-full justify-between border-transparent bg-transparent px-0 shadow-none" size="sm">
        <span className="flex min-w-0 items-center gap-1.5 truncate text-left" data-slot="select-value">
          {selectedOption ? <span className="size-2 rounded-full" style={{ backgroundColor: selectedOption.color }} /> : null}
          {selectedOption?.value ?? t("common.notSelected")}
        </span>
      </SelectTrigger>
      <SelectContent alignItemWithTrigger={false} className="min-w-44">
        <SelectGroup>
          {field.options.map((option) => (
            <SelectItem key={option.id} value={option.id}>
              <span className="size-2 rounded-full" style={{ backgroundColor: option.color }} />
              {option.value}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function FieldOptionChecklist({
  field,
  onChange,
  selectedValues,
}: {
  field: WorkObjectFieldDefinition;
  onChange: (value: string[]) => void;
  selectedValues: string[];
}) {
  const t = useT();
  if (field.options.length === 0) {
    return <span className="py-1 text-muted-foreground">{t("board.noOptions")}</span>;
  }

  return (
    <div className="flex min-w-0 flex-col gap-1">
      {field.options.map((option) => (
        <label key={option.id} className="flex min-h-7 items-center gap-2 rounded-md px-2 text-xs hover:bg-muted/50">
          <Checkbox
            checked={selectedValues.includes(option.id)}
            onCheckedChange={(checked) =>
              onChange(checked ? [...selectedValues, option.id] : selectedValues.filter((selectedValue) => selectedValue !== option.id))
            }
          />
          <span className="size-2 rounded-full" style={{ backgroundColor: option.color }} />
          <span className="min-w-0 truncate">{option.value}</span>
        </label>
      ))}
    </div>
  );
}

function inputTypeForField(type: WorkObjectFieldDefinition["type"]) {
  switch (type) {
    case "number":
    case "currency":
    case "percent":
      return "number";
    case "date":
      return "date";
    case "url":
      return "url";
    case "email":
      return "email";
    case "phone":
      return "tel";
    default:
      return "text";
  }
}

function typeBadgeClass(categoryKey?: string) {
  switch (categoryKey) {
    case "discovery":
      return "border-cyan-500/25 bg-cyan-500/12 text-cyan-200 hover:bg-cyan-500/18";
    case "planning":
      return "border-violet-500/25 bg-violet-500/12 text-violet-200 hover:bg-violet-500/18";
    case "operations":
      return "border-emerald-500/25 bg-emerald-500/12 text-emerald-200 hover:bg-emerald-500/18";
    default:
      return "border-amber-500/25 bg-amber-500/15 text-amber-200 hover:bg-amber-500/20";
  }
}

function typeDotClass(categoryKey?: string) {
  switch (categoryKey) {
    case "discovery":
      return "bg-cyan-300";
    case "planning":
      return "bg-violet-300";
    case "operations":
      return "bg-emerald-300";
    default:
      return "bg-amber-300";
  }
}
